import React from "react";
import { useState, useEffect, useRef } from "react";
import { IconButton, Popover } from "@mui/material";
import {
  Mood as MoodIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react";
import { TbMessage2Up } from "react-icons/tb";

const ChatInput = ({
  value,
  onChange,
  onSend,
  onRequestContact = () => {},
  placeholder = "Escribe un mensaje",
  disabled = false,
  contactRequestDisabled = false,
  isDarkMode = false,
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 200)}px`;
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) {
        onSend();
        setShowEmojiPicker(false);
      }
    }
  };

  const handleSendClick = () => {
    if (!disabled && value.trim()) {
      onSend();
      setShowEmojiPicker(false);
    }
  };

  const handleToggleEmojiPicker = () => {
    if (!disabled) {
      setShowEmojiPicker((prev) => !prev);
    }
  };

  const handleRequestContactClick = () => {
    if (!contactRequestDisabled) {
      onRequestContact();
      setShowEmojiPicker(false);
    }
  };

  const handleEmojiClick = (emojiData) => {
    onChange(value + emojiData.emoji);
  };

  useEffect(() => {
    if (disabled) {
      setShowEmojiPicker(false);
    }
  }, [disabled]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div style={styles.container}>
      <style>{`
        .chat-input-textarea {
          scrollbar-width: thin !important;
          scrollbar-color: var(--chat-scrollbar-thumb, #c1c1c1) transparent !important;
        }
        .chat-input-textarea::-webkit-scrollbar {
          width: 6px !important;
          height: 6px !important;
          display: block !important;
        }
        .chat-input-textarea::-webkit-scrollbar-track {
          background: transparent !important;
        }
        .chat-input-textarea::-webkit-scrollbar-thumb {
          background-color: var(--chat-scrollbar-thumb, #c1c1c1) !important;
          border-radius: 3px !important;
        }
      `}</style>
      {showEmojiPicker && (
        <div ref={pickerRef} style={styles.emojiPickerContainer}>
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={isDarkMode ? "dark" : "light"}
            width={320}
            height={380}
            searchDisabled={false}
            skinTonesDisabled={true}
          />
        </div>
      )}

      <div style={styles.leftActions}>
        <IconButton
          size="small"
          style={styles.iconBtn}
          disabled={disabled}
          onClick={handleToggleEmojiPicker}
        >
          <MoodIcon style={styles.icon} />
        </IconButton>
        <IconButton
          size="small"
          style={styles.iconBtn}
          disabled={contactRequestDisabled}
          onClick={handleRequestContactClick}
          aria-label="Solicitar contacto por WhatsApp"
        >
          <TbMessage2Up style={{ ...styles.icon }} />
        </IconButton>{" "}
      </div>

      <div style={styles.inputWrapper}>
        <textarea
          ref={textareaRef}
          className="chat-input-textarea"
          style={styles.input}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
        />
      </div>

      <div style={styles.rightActions}>
        <IconButton
          size="small"
          style={{
            ...styles.sendBtn,
            backgroundColor:
              value.trim() && !disabled ? "#00a884" : "transparent",
          }}
          onClick={handleSendClick}
          disabled={disabled || !value.trim()}
        >
          <SendIcon
            style={{
              ...styles.sendIcon,
              color:
                value.trim() && !disabled
                  ? "#ffffff"
                  : "var(--chat-icon, #54656f)",
            }}
          />
        </IconButton>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "flex-end",
    padding: "8px 12px",
    backgroundColor: "var(--chat-bg-header, #f0f2f5)",
    borderTop: "1px solid var(--chat-border, #e9edef)",
    gap: "10px",
    position: "relative",
  },
  emojiPickerContainer: {
    position: "absolute",
    bottom: "100%",
    left: "12px",
    marginBottom: "8px",
    zIndex: 1000,
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    overflow: "hidden",
  },
  leftActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  iconBtn: {
    padding: "6px",
  },
  icon: {
    color: "var(--chat-icon, #54656f)",
    fontSize: "24px",
  },
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  input: {
    width: "100%",
    padding: "9px 24px 9px 12px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "var(--chat-bg-chat-input, #ffffff)",
    outline: "none",
    fontSize: "14px",
    color: "var(--chat-text-primary, #111b21)",
    fontFamily: "Raleway, sans-serif",
    resize: "none",
    overflowY: "auto",
    maxHeight: "200px",
    minHeight: "38px",
    lineHeight: "1.4",
    boxSizing: "border-box",
  },
  rightActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  sendBtn: {
    padding: "8px",
    borderRadius: "50%",
    transition: "background-color 0.2s ease, transform 0.1s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  sendIcon: {
    fontSize: "20px",
  },
};

export default ChatInput;
